/**
 * OAuth callback handler for OpenAI and Antigravity providers
 * Handles authorization code exchange and redirects to home
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth.ts";
import type { AuthTokens } from "@/lib/auth/tokens.ts";
import type { AIProvider } from "@/lib/auth/tokens.ts";

export function AuthCallback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleOAuthCallback();
  }, []);

  const handleOAuthCallback = () => {
    try {
      // Check for success connection
      const connected = searchParams.get("connected");
      const authError = searchParams.get("authError");
      const storedProvider = sessionStorage.getItem(
        "oauth_provider",
      ) as AIProvider | null;

      if (authError) {
        throw new Error(authError);
      }

      if (connected) {
        // Map backend provider name to our AIProvider type
        // Backend uses "google", we use "antigravity"
        let provider: AIProvider;
        if (connected === "google") {
          provider = "antigravity";
        } else {
          throw new Error(`Unknown provider: ${connected}`);
        }

        // Verify this matches what we stored (if available)
        if (storedProvider && storedProvider !== provider) {
          console.warn(
            `Stored provider (${storedProvider}) doesn't match callback provider (${provider})`,
          );
        }

        // Backend handled the token exchange and stored it in cookies
        // The actual token is in httpOnly cookies, but we need to save metadata to localStorage
        // so the UI can show connection status and send the provider to the backend
        const tokens: AuthTokens = {
          provider,
          // Token is actually in backend cookie, we send a marker to indicate cookie-based auth
          accessToken: "use_backend_cookie",
          expiresAt: Date.now() + 3600 * 1000, // 1 hour default
        };

        // Clean up session storage
        sessionStorage.removeItem("oauth_provider");

        // Save tokens and redirect
        login(tokens);
        navigate("/", { replace: true });
        return;
      }

      // If no success or error, something went wrong
      throw new Error("No callback parameters received");
    } catch (err) {
      console.error("OAuth callback error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");

      // Clean up on error
      sessionStorage.removeItem("oauth_provider");

      // Redirect to home after 3 seconds
      setTimeout(() => navigate("/", { replace: true }), 3000);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center max-w-md">
        {error
          ? (
            <>
              <h1 className="text-2xl font-bold mb-4 text-destructive">
                {t("auth.error")}
              </h1>
              <p className="text-muted-foreground mb-4">{error}</p>
              <p className="text-sm text-muted-foreground">
                {t("auth.redirecting")}
              </p>
            </>
          )
          : (
            <>
              <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-4">
                {t("auth.processingAuth")}
              </h1>
              <p className="text-muted-foreground">
                {t("auth.pleaseWait")}
              </p>
            </>
          )}
      </div>
    </div>
  );
}
